import { useLocation } from "react-router-dom";
import { Search, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui-store";
import { useOrgBranding } from "@/hooks/use-org-branding";
import { NotificationBell } from "@/components/nav/NotificationBell";

const routeLabels: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/studies": "Studies",
  "/sites": "Sites",
  "/subjects": "Subjects",
  "/queries": "Queries",
  "/chat": "AI Chat",
  "/calendar": "Calendar",
  "/alerts": "Alerts",
  "/integrations": "Integrations",
  "/users": "Users & Roles",
  "/settings": "Settings",
  "/billing": "Billing",
  "/audit": "Audit Trail",
  "/admin/organizations": "Organizations",
  "/studies/new": "New Study",
};

function getPageName(pathname: string): string {
  if (routeLabels[pathname]) return routeLabels[pathname];
  const match = Object.entries(routeLabels).find(([route]) =>
    pathname.startsWith(route + "/"),
  );
  return match ? match[1] : "Page";
}

export function TopBar({ className }: { className?: string }) {
  const location = useLocation();
  const pageName = getPageName(location.pathname);
  const { openCommandPalette, toggleMobileMenu } = useUIStore();
  const { data: branding } = useOrgBranding();

  return (
    <header
      className={cn(
        "flex h-14 shrink-0 items-center justify-between border-b border-border bg-background px-4 md:px-6",
        className,
      )}
    >
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={toggleMobileMenu}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="text-muted-foreground">{branding?.displayName ?? "MedTechOS"}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-medium text-foreground">{pageName}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={openCommandPalette}
          className="flex h-9 items-center gap-2 rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
          aria-label="Search"
          data-testid="search-button"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
          <kbd className="ml-2 hidden rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
            &#8984;K
          </kbd>
        </button>

        <NotificationBell />
      </div>
    </header>
  );
}
